import { useMemo, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { getIDFromUrl, Url } from '@rossum/api-client';
import {
  IconCirclePlus,
  IconCopy,
  IconEdit,
  IconFolderSymlink,
  IconSettings,
  IconTrash,
} from '@rossum/ui/icons/tabler';
import {
  Chip,
  Dialog,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  Stack,
  SvgIcon,
  Tooltip,
} from '@rossum/ui/material';
import { userHasQueueId } from '../../../../redux/modules/groups/helpers';
import {
  isEmpoweredManager,
  isUserAdmin,
} from '../../../../redux/modules/user/selectors';
import { State } from '../../../../types/state';
import { useScrollToElementOnLoad } from '../../../../utils/hooks/useScrollToElementOnLoad';
import { filterWorkspaces } from '../../../queues/helpers/filterWorkspaces';
import { useWorkspacesWithQueues } from '../../../queues/hooks/useWorkspacesWithQueues';
import { AddQueueDialog } from '../../add-queue/AddQueueDialog';
import { EmptySidebar } from '../../sidebar/components/EmptySidebar';
import { useSortWorkspacesWithQueues } from '../../sidebar/hooks/useSortWorkspacesWithQueues';
import { useWorkspaceSorting } from '../../sidebar/hooks/useWorkspaceSorting';
import {
  SidebarDropdownState,
  sidebarIdleState,
  SidebarState,
} from '../SidebarState';
import { QueueDelete } from './QueueDelete';
import { QueueDuplicate } from './QueueDuplicate';
import { QueueMove } from './QueueMove';
import { Workspace } from './Workspace';
import { WorkspaceDelete } from './WorkspaceDelete';
import { WorkspacesSkeletons } from './WorkspacesSkeletons';

type SidebarQueuesProps = {
  currentQueueId?: number;
  onQueueChange: (queueId: number) => void;
  setAllDocumentsLevelActive: () => void;
  searchValue: string;
};

export const SidebarQueues = ({
  currentQueueId,
  onQueueChange,
  setAllDocumentsLevelActive,
  searchValue,
}: SidebarQueuesProps) => {
  const intl = useIntl();
  const editable = useSelector(isUserAdmin);
  const empoweredManager = useSelector(isEmpoweredManager);
  const userQueueUrls = useSelector((state: State) => state.user.queues);
  const history = useHistory();

  const {
    allWorkspacesWithQueues: unsortedWorkspaces,
    isLoading,
    queues,
    workspaces,
  } = useWorkspacesWithQueues({
    enableQueries: true,
    refetchInterval: 20 * 1000,
    hideDisabledQueues: true,
  });

  const { sorting } = useWorkspaceSorting();
  const { sortedWorkspaces } = useSortWorkspacesWithQueues({
    workspaces: unsortedWorkspaces ?? [],
    sorting,
  });

  const filteredWorkspaces = useMemo(() => {
    if (!searchValue) return sortedWorkspaces;
    return filterWorkspaces(searchValue, sortedWorkspaces);
  }, [searchValue, sortedWorkspaces]);

  const queuesLoaded = !isLoading;

  // TODO: Rewrite whole logic of automatic scrolling in the sidebar
  const { refCallback } = useScrollToElementOnLoad({
    behavior: 'smooth',
    resetAutomatically: true,
  });

  const [sidebarState, setSidebarState] =
    useState<SidebarState>(sidebarIdleState);

  const canSeeQueueSettings =
    editable ||
    (empoweredManager &&
      'queueId' in sidebarState &&
      userHasQueueId(Number(sidebarState.queueId), userQueueUrls));

  const renameButtonClicked = (prevSidebarState: SidebarDropdownState) => {
    setSidebarState({
      ...prevSidebarState,
      action: 'editing',
    });
  };

  const moveQueueButtonClicked = (queueId: number, workspaceUrl: Url) => {
    setSidebarState({
      queueId,
      targetWorkspaceUrl: workspaceUrl,
      action: 'moveQueue',
    });
  };

  const [draggedName, setDraggedName] = useState<string | null>(null);

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setDraggedName(null);
    const targetWorkspaceUrl: Url | undefined = over?.data.current?.['url'];

    if (
      targetWorkspaceUrl &&
      active.data.current?.['workspaceUrl'] !== targetWorkspaceUrl
    ) {
      setSidebarState({
        queueId: active.data.current?.['id'],
        targetWorkspaceUrl,
        action: 'moveQueue',
      });
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    const name: string | null = event.active.data.current?.['name'] ?? null;
    setDraggedName(name);
  };

  const deleteButtonClicked = (prevSidebarState: SidebarDropdownState) => {
    setSidebarState({
      ...prevSidebarState,
      action: 'deleting',
    });
  };

  const addQueueButtonClicked = (workspaceUrl: Url) => {
    setSidebarState({
      workspaceId: getIDFromUrl(workspaceUrl),
      workspaceUrl,
      action: 'adding',
    });
  };

  const duplicateQueueButtonClicked = (queueId: number) => {
    setSidebarState({
      queueId,
      action: 'duplicate',
    });
  };

  const setIdleState = () => setSidebarState(sidebarIdleState);

  const onSuccessQueueDelete = (deletedQueueId: number) => {
    if (currentQueueId === deletedQueueId) {
      setAllDocumentsLevelActive();
    }

    setIdleState();
  };

  const onSuccessDuplicateDelete = (duplicatedQueueId: number) => {
    onQueueChange(duplicatedQueueId);
    setIdleState();
  };

  const workspaceUrl =
    'workspaceUrl' in sidebarState ? sidebarState.workspaceUrl : undefined;
  const workspaceId = workspaceUrl ? getIDFromUrl(workspaceUrl) : undefined;

  const dropdownOpen = sidebarState.action === 'dropdown';
  const dropdownAnchorEl = dropdownOpen ? sidebarState.anchorEl : null;
  const dropdownWorkspaceHasQueues =
    dropdownOpen &&
    !sidebarState.queueId &&
    !!filteredWorkspaces?.find(workspace => workspace.id === workspaceId)
      ?.queues.length;

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 10 },
  });
  const sensors = useSensors(pointerSensor);

  const queueToMove =
    sidebarState.action === 'moveQueue'
      ? queues?.find(queue => queue.id === sidebarState.queueId)
      : undefined;

  const queueToDuplicate =
    sidebarState.action === 'duplicate'
      ? queues?.find(queue => queue.id === sidebarState.queueId)
      : undefined;

  const workspaceToDelete =
    sidebarState.action === 'deleting'
      ? workspaces?.find(workspace => workspace.id === sidebarState.workspaceId)
      : undefined;

  return (
    <Stack flex={1} overflow="auto">
      <DndContext
        onDragEnd={handleDragEnd}
        onDragStart={handleDragStart}
        sensors={sensors}
      >
        <List
          sx={{ pl: 0, paddingTop: 0, paddingBottom: 0, flex: 1 }}
          component="nav"
          aria-labelledby="queues-list"
        >
          {queuesLoaded ? (
            filteredWorkspaces.length !== 0 ? (
              filteredWorkspaces.map(workspace => {
                const active = workspace.queues.some(
                  ({ id }) => id === currentQueueId
                );

                return (
                  <Workspace
                    setQueueRef={active ? refCallback : undefined}
                    key={workspace.id}
                    id={workspace.id}
                    url={workspace.url}
                    name={workspace.name}
                    currentQueueId={currentQueueId}
                    queues={workspace.queues}
                    toReviewCount={workspace.toReviewCount}
                    selectQueue={(queueId: number) => onQueueChange(queueId)}
                    active={active}
                    editable={editable}
                    empoweredManager={empoweredManager}
                    userQueueUrls={userQueueUrls}
                    // Only selected workspace receives a state, the other ones always get reset.
                    sidebarState={
                      workspaceUrl === workspace.url
                        ? sidebarState
                        : sidebarIdleState
                    }
                    searchValue={searchValue}
                    setSidebarState={setSidebarState}
                  />
                );
              })
            ) : (
              <EmptySidebar searchValue={searchValue} />
            )
          ) : (
            <WorkspacesSkeletons />
          )}
        </List>
        <DragOverlay modifiers={[snapCenterToCursor]}>
          {draggedName ? (
            <Chip
              color="info"
              icon={
                <SvgIcon fontSize="medium">
                  <IconFolderSymlink />
                </SvgIcon>
              }
              label={draggedName}
              sx={{ pl: 0.75, cursor: 'grabbing' }}
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      <Menu
        anchorEl={dropdownAnchorEl}
        open={dropdownOpen}
        autoFocus
        MenuListProps={{ dense: true }}
        onClose={setIdleState}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transitionDuration={{
          exit: 0,
          enter: 250,
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {dropdownOpen && !sidebarState.queueId && editable ? (
          <ListItem disablePadding>
            <ListItemButton
              onClick={() =>
                dropdownOpen && addQueueButtonClicked(sidebarState.workspaceUrl)
              }
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <SvgIcon fontSize="medium">
                  <IconCirclePlus />
                </SvgIcon>
              </ListItemIcon>
              <ListItemText>
                {intl.formatMessage({
                  id: 'components.workspaces.menu.labels.addQueue',
                })}
              </ListItemText>
            </ListItemButton>
          </ListItem>
        ) : null}
        {editable ? (
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => dropdownOpen && renameButtonClicked(sidebarState)}
              data-cy="workspace-rename-menu-item"
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <SvgIcon fontSize="medium">
                  <IconEdit />
                </SvgIcon>
              </ListItemIcon>
              <ListItemText>
                {intl.formatMessage({
                  id: 'components.workspaces.menu.labels.rename',
                })}
              </ListItemText>
            </ListItemButton>
          </ListItem>
        ) : null}
        {dropdownOpen && sidebarState.queueId && editable ? (
          <ListItem disablePadding data-cy="queue-move-menu-item">
            <ListItemButton
              onClick={() =>
                sidebarState.queueId &&
                moveQueueButtonClicked(
                  sidebarState.queueId,
                  sidebarState.workspaceUrl
                )
              }
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <SvgIcon fontSize="medium">
                  <IconFolderSymlink />
                </SvgIcon>
              </ListItemIcon>
              <ListItemText>
                {intl.formatMessage({
                  id: 'components.workspaces.menu.labels.move',
                })}
              </ListItemText>
            </ListItemButton>
          </ListItem>
        ) : null}
        {dropdownOpen && sidebarState.queueId && editable ? (
          <ListItem disablePadding data-cy="queue-duplicate-menu-item">
            <ListItemButton
              onClick={() =>
                sidebarState.queueId &&
                duplicateQueueButtonClicked(sidebarState.queueId)
              }
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <SvgIcon fontSize="medium">
                  <IconCopy />
                </SvgIcon>
              </ListItemIcon>
              <ListItemText>
                {intl.formatMessage({
                  id: 'components.workspaces.menu.labels.duplicateQueue',
                })}
              </ListItemText>
            </ListItemButton>
          </ListItem>
        ) : null}
        {editable ? (
          <Tooltip
            title={
              dropdownWorkspaceHasQueues
                ? intl.formatMessage({
                    id: 'components.workspaces.menu.errors.deleteForbidden',
                  })
                : ''
            }
            componentsProps={{ tooltip: { sx: { textAlign: 'left' } } }}
            placement="right"
          >
            <ListItem disablePadding data-cy="workspace-delete-menu-item">
              <ListItemButton
                disableRipple={dropdownWorkspaceHasQueues}
                disabled={dropdownWorkspaceHasQueues}
                onClick={
                  dropdownWorkspaceHasQueues
                    ? e => e.preventDefault()
                    : () => dropdownOpen && deleteButtonClicked(sidebarState)
                }
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <SvgIcon fontSize="medium">
                    <IconTrash />
                  </SvgIcon>
                </ListItemIcon>
                <ListItemText>
                  {intl.formatMessage({
                    id: 'components.workspaces.menu.labels.delete',
                  })}
                </ListItemText>
              </ListItemButton>
            </ListItem>
          </Tooltip>
        ) : null}
        {dropdownOpen && sidebarState.queueId && canSeeQueueSettings ? (
          <ListItem disablePadding data-cy="queue-settings-menu-item">
            <ListItemButton
              onClick={(e: React.MouseEvent) => {
                const route = `/queues/${sidebarState.queueId}/settings`;

                if (e.metaKey || e.ctrlKey) {
                  return window.open(route, '_blank');
                }

                e.preventDefault();

                return history.push(route, {
                  backLink: history.location.pathname,
                });
              }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <SvgIcon fontSize="medium">
                  <IconSettings />
                </SvgIcon>
              </ListItemIcon>
              <ListItemText>
                {intl.formatMessage({
                  id: 'components.workspaces.menu.labels.settings',
                })}
              </ListItemText>
            </ListItemButton>
          </ListItem>
        ) : null}
      </Menu>
      <Dialog
        open={sidebarState.action === 'deleting'}
        onClose={setIdleState}
        PaperProps={{
          sx: { width: 480, minHeight: 213, position: 'fixed' },
          elevation: 2,
        }}
      >
        <>
          {sidebarState.action === 'deleting' &&
          sidebarState.queueId &&
          sidebarState.queueName ? (
            <QueueDelete
              queueId={sidebarState.queueId}
              queueName={sidebarState.queueName}
              onDelete={onSuccessQueueDelete}
              onCancel={setIdleState}
            />
          ) : null}
          {sidebarState.action === 'deleting' &&
          !sidebarState.queueId &&
          workspaceToDelete ? (
            <WorkspaceDelete
              workspace={workspaceToDelete}
              onDelete={setIdleState}
              onCancel={setIdleState}
            />
          ) : null}
        </>
      </Dialog>
      <Dialog
        open={sidebarState.action === 'moveQueue'}
        PaperProps={{
          sx: { width: 480, minHeight: 213, position: 'fixed' },
          elevation: 2,
        }}
        onClose={setIdleState}
      >
        {sortedWorkspaces.length &&
        sidebarState.action === 'moveQueue' &&
        queueToMove ? (
          <QueueMove
            queue={queueToMove}
            workspaces={sortedWorkspaces}
            targetWorkspaceUrl={sidebarState.targetWorkspaceUrl}
            onMove={setIdleState}
            onCancel={setIdleState}
          />
        ) : null}
      </Dialog>
      <Dialog
        open={sidebarState.action === 'duplicate'}
        onClose={setIdleState}
        PaperProps={{
          sx: { width: 480, minHeight: 300, position: 'fixed' },
          elevation: 2,
        }}
      >
        {queueToDuplicate && sidebarState.action === 'duplicate' ? (
          <QueueDuplicate
            queue={queueToDuplicate}
            onDuplicate={onSuccessDuplicateDelete}
            onCancel={setIdleState}
          />
        ) : null}
      </Dialog>
      <AddQueueDialog
        key={`addDialogKey-${sidebarState.action === 'adding'}`}
        open={sidebarState.action === 'adding'}
        onClose={setIdleState}
        workspaceUrl={workspaceUrl}
        onSuccess={onQueueChange}
        currentQueueId={currentQueueId}
      />
    </Stack>
  );
};
